import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { PageLayout } from "../ui-components/PageLayout";
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  Button,
  Paper,
  Alert,
  Chip,
  InputAdornment,
} from "@mui/material";
import { NumericFormat } from "react-number-format";
import {
  PlayArrow as PlanNowIcon,
  PictureAsPdf as PdfIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store/reducers";
import { AppDispatch } from "../store/store";
import { getBusinessPlanningData } from "../store/modules/analytics/analyticsActions";
import { clearBusinessPlanningData } from "../store/modules/analytics/analyticsStateActions";
import LoadingDialog from "../ui-components/Dialogs/LoadingDialog";
import {
  BusinessPlanningCategoryType,
  IBrokerageAnniversaryModel,
  IBusinessPlanningCategoryProjection,
} from "../types/types";
import {
  calculateProjectedEffectiveGci,
  calculateProjectedGci,
  generateCategoryProjections,
  generateProjectedGciTrend,
} from "../utils/BusinessPlanningCalculator";
import { CategoryProjectionCard } from "../ui-components/Charts/CategoryProjectionCard";
import { BusinessPlanningTrendChart } from "../ui-components/Charts/BusinessPlanningTrendChart";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { formatCurrency } from "../utils/common";
import { CoAgentFilterSelect } from "../ui-components/CoAgentFilterSelect";
import { usePageCoAgentFilter } from "../hooks/usePageCoAgentFilter";

const categoryLabels: Record<BusinessPlanningCategoryType, string> = {
  type: "Type",
  source: "Source",
  reason: "Reason",
  zipCode: "Zip Code",
  county: "County",
  customArea: "Custom Area",
};

const categoryDescriptions: Record<BusinessPlanningCategoryType, string> = {
  type: "Buyer vs Seller breakdown",
  source: "Lead source breakdown (SOI, Online, etc.)",
  reason: "Transaction reason breakdown (Upsizing, Investment, etc.)",
  zipCode: "Geographic breakdown by zip code",
  county: "Geographic breakdown by county",
  customArea: "Breakdown by your custom defined areas",
};

const BusinessPlanning: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const resultsRef = useRef<HTMLDivElement>(null);
  const { hiddenFields } = useSelector(
    (state: RootState) => state.hiddenFields,
  );
  const { selectedCoAgentIds } = useSelector(
    (state: RootState) => state.coAgentFilter,
  );
  const { data: userData } = useSelector(
    (state: RootState) => state.commonData.userData,
  );

  const coAgents = userData?.coAgents || [];

  // Page-specific co-agent filter
  const [effectiveCoAgentIds, setPageCoAgentIds, isGlobalFilterActive] =
    usePageCoAgentFilter("businessPlanning", selectedCoAgentIds);

  // Redux state
  const { years } = useSelector((state: RootState) => state.transactionYear);
  const businessPlanning = useSelector(
    (state: RootState) => state.analytics?.businessPlanning,
  );
  const { brokerageAnniversaries } = useSelector(
    (state: RootState) => state.financialSettings,
  );

  // Only past years can be evaluated (no current or future year)
  const sortedYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [...years]
      .filter((y) => (y.year ?? 0) < currentYear)
      .sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
  }, [years]);
  // Form state
  const [targetNetIncome, setTargetNetIncome] = useState<string>("");
  const [selectedYearIds, setSelectedYearIds] = useState<number[]>([]);
  const [forecastYear, setForecastYear] = useState<"thisYear" | "nextYear">(
    "thisYear",
  );
  const [selectedCategories, setSelectedCategories] = useState<
    BusinessPlanningCategoryType[]
  >(["type"]);

  // UI state
  const [hasCalculated, setHasCalculated] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  // Helper to extract year from anniversary date (handles string, Date, or moment)
  const getYearFromDate = (date: any): number | null => {
    if (!date) return null;
    // If it's a moment object with .year() method
    if (typeof date.year === "function") {
      return date.year();
    }
    // If it's a string or Date, parse it
    const parsed = new Date(date);
    if (!isNaN(parsed.getTime())) {
      return parsed.getFullYear();
    }
    return null;
  };

  // Get the brokerage anniversary for the forecast year
  const getForecastYearBrokerageAnniversary =
    useCallback((): IBrokerageAnniversaryModel | null => {
      if (
        !brokerageAnniversaries.data ||
        brokerageAnniversaries.data.length === 0
      ) {
        return null;
      }

      const currentYear = new Date().getFullYear();
      const targetYear =
        forecastYear === "thisYear" ? currentYear : currentYear + 1;

      // Find anniversary that applies to the target year
      // Sort by anniversary date descending and find the most recent one before or during target year
      const sortedAnniversaries = [...brokerageAnniversaries.data].sort(
        (a, b) => {
          const dateA = getYearFromDate(a.anniversaryDate) || 0;
          const dateB = getYearFromDate(b.anniversaryDate) || 0;
          return dateB - dateA;
        },
      );

      return (
        sortedAnniversaries.find((ann) => {
          const annYear = getYearFromDate(ann.anniversaryDate);
          return annYear !== null && annYear <= targetYear;
        }) ||
        sortedAnniversaries[sortedAnniversaries.length - 1] ||
        null
      );
    }, [brokerageAnniversaries.data, forecastYear]);

  const handleCategoryChange = (event: any) => {
    const value = event.target.value as BusinessPlanningCategoryType[];
    // Limit to 3 categories
    if (value.length <= 3) {
      setSelectedCategories(value);
    }
  };

  const handlePlanNow = () => {
    if (
      !targetNetIncome ||
      selectedYearIds.length === 0 ||
      selectedCategories.length === 0
    ) {
      return;
    }

    dispatch(
      getBusinessPlanningData(
        selectedYearIds,
        forecastYear,
        selectedCategories,
        true,
        effectiveCoAgentIds,
      ),
    );
    setHasCalculated(true);
  };

  const handleReset = () => {
    dispatch(clearBusinessPlanningData());
    setHasCalculated(false);
  };

  // Re-fetch when co-agent filter changes
  useEffect(() => {
    if (
      hasCalculated &&
      targetNetIncome &&
      selectedYearIds.length > 0 &&
      selectedCategories.length > 0
    ) {
      dispatch(
        getBusinessPlanningData(
          selectedYearIds,
          forecastYear,
          selectedCategories,
          true,
          effectiveCoAgentIds,
        ),
      );
    }
  }, [
    effectiveCoAgentIds,
    hasCalculated,
    targetNetIncome,
    selectedYearIds,
    selectedCategories,
    forecastYear,
    dispatch,
  ]);

  // Calculate projections when data is available
  const projections = useMemo(() => {
    if (!businessPlanning?.data || !targetNetIncome) {
      return null;
    }

    const data = businessPlanning.data;
    const targetIncome = parseFloat(targetNetIncome) || 0;
    const brokerageAnniversary = getForecastYearBrokerageAnniversary();

    // Calculate projected effective GCI and projected GCI
    const projectedEffectiveGci = calculateProjectedEffectiveGci(
      targetIncome,
      brokerageAnniversary,
    );

    const firstCategory = data.categories?.[0];
    const totalGci = firstCategory?.totalGci || 0;
    const totalEffectiveGci = firstCategory?.totalEffectiveGci || 0;

    const projectedGci = calculateProjectedGci(
      projectedEffectiveGci,
      totalGci,
      totalEffectiveGci,
    );

    // Generate category projections
    const categoryProjections: IBusinessPlanningCategoryProjection[] =
      data.categories?.map((categoryData) => {
        const ytdData = data.ytdData?.find(
          (y) => y.category === categoryData.category,
        );
        return generateCategoryProjections(categoryData, projectedGci, ytdData);
      }) ?? [];

    const projectedTrend = generateProjectedGciTrend(
      data.monthlyTrend!,
      totalGci,
      projectedGci,
    );

    return {
      targetNetIncome: targetIncome,
      projectedEffectiveGci,
      projectedGci,
      categoryProjections,
      projectedTrend,
      currentYearTrend: data.currentYearMonthlyTrend,
      brokerageAnniversary,
    };
  }, [
    businessPlanning?.data,
    targetNetIncome,
    getForecastYearBrokerageAnniversary,
  ]);

  const handleExportPdf = async () => {
    if (!resultsRef.current) return;

    setIsExportingPdf(true);
    try {
      // Load the logo image
      const logoImg = new Image();
      logoImg.crossOrigin = "anonymous";
      const logoLoaded = new Promise<void>((resolve, reject) => {
        logoImg.onload = () => resolve();
        logoImg.onerror = () => reject(new Error("Failed to load logo"));
        logoImg.src = "/logo.png";
      });

      await logoLoaded;

      const canvas = await html2canvas(resultsRef.current, {
        scale: 2,
        logging: false,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");

      // Calculate dimensions
      const headerHeight = 280; // Height for logo header and parameters
      const pageWidth = canvas.width;
      const pageHeight = canvas.height + headerHeight;

      const pdf = new jsPDF({
        orientation: pageWidth > pageHeight ? "landscape" : "portrait",
        unit: "px",
        format: [pageWidth, pageHeight],
      });

      // Add logo at the top left - maintain aspect ratio
      const logoMaxHeight = 140;
      const logoAspectRatio = logoImg.width / logoImg.height;
      const logoHeight = logoMaxHeight;
      const logoWidth = logoHeight * logoAspectRatio;
      const logoX = 20;
      const logoY = 20;

      // Create a canvas to get logo as data URL
      const logoCanvas = document.createElement("canvas");
      logoCanvas.width = logoImg.width;
      logoCanvas.height = logoImg.height;
      const logoCtx = logoCanvas.getContext("2d");
      if (logoCtx) {
        logoCtx.drawImage(logoImg, 0, 0);
        const logoDataUrl = logoCanvas.toDataURL("image/png");
        pdf.addImage(logoDataUrl, "PNG", logoX, logoY, logoWidth, logoHeight);
      }

      // Add title to the right of the logo
      const planYear =
        forecastYear === "thisYear"
          ? new Date().getFullYear()
          : new Date().getFullYear() + 1;
      const title = `Business Plan ${planYear}`;
      pdf.setFontSize(48);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(33, 37, 41); // Dark gray color
      const titleX = logoX + logoWidth + 40;
      const titleY = logoY + logoHeight / 2 - 10; // Raised up to make room for parameters
      pdf.text(title, titleX, titleY);

      // Add today's date at top right
      pdf.setFontSize(24);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(100, 100, 100);
      const today = new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      const dateText = `Generated: ${today}`;
      const dateWidth = pdf.getTextWidth(dateText);
      pdf.text(dateText, pageWidth - dateWidth - 20, 30);

      // Add client-entered parameters below the title
      pdf.setFontSize(20);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(100, 100, 100); // Gray color for parameters

      const paramsY = titleY + 35;
      const paramLineHeight = 28;

      // Target Net Income
      pdf.setFontSize(24);
      const formattedIncome = formatCurrency(parseFloat(targetNetIncome) || 0);
      pdf.text(`Target Net Income: ${formattedIncome}`, titleX, paramsY);

      // Evaluation Years
      const yearsText = selectedYearIds
        .map((id) => years.find((y) => y.id === id)?.year)
        .filter(Boolean)
        .join(", ");
      pdf.text(
        `Evaluation Years: ${yearsText}`,
        titleX,
        paramsY + paramLineHeight,
      );

      // Forecast For
      const forecastText =
        forecastYear === "thisYear"
          ? `Current Year (${new Date().getFullYear()}) - Projected and Actual`
          : `Next Year (${new Date().getFullYear() + 1}) - Projected`;
      pdf.text(
        `Forecast For: ${forecastText}`,
        titleX,
        paramsY + paramLineHeight * 2,
      );

      // Categories
      const categoriesText = selectedCategories
        .map((cat) => categoryLabels[cat])
        .join(", ");
      pdf.text(
        `Categories: ${categoriesText}`,
        titleX,
        paramsY + paramLineHeight * 3,
      );

      // Add the content below the header
      pdf.addImage(
        imgData,
        "PNG",
        0,
        headerHeight,
        canvas.width,
        canvas.height,
      );

      pdf.save(
        `Business_Plan_${
          forecastYear === "thisYear"
            ? new Date().getFullYear()
            : new Date().getFullYear() + 1
        }.pdf`,
      );
    } catch (error) {
      console.error("Error exporting PDF:", error);
    } finally {
      setIsExportingPdf(false);
    }
  };

  const selectedYearText =
    selectedYearIds.length > 0
      ? selectedYearIds
          .map((id) => years.find((y) => y.id === id)?.year)
          .join(", ")
      : "Select years to evaluate";

  const isFormValid =
    targetNetIncome &&
    parseFloat(targetNetIncome) > 0 &&
    selectedYearIds.length > 0 &&
    selectedCategories.length > 0;

  return (
    <PageLayout>
      <LoadingDialog
        open={businessPlanning?.loading || isExportingPdf}
        message={
          isExportingPdf ? "Generating PDF..." : "Calculating Business Plan..."
        }
      />

      <Box sx={{ mb: 6, ml: 1 }}>
        <Typography
          variant="h4"
          color="primary"
          component="h1"
          fontWeight="bold"
          gutterBottom
        >
          Business Planning
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3, fontWeight: 400 }}
        >
          Agents Intel helps you plan your business based on your unique proven
          record of past sales. This is where your business' past meets its
          future!
        </Typography>

        {/* Important Notes */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Note that the following analysis only accounts for{" "}
            <strong>Seller</strong> and <strong>Buyer</strong> transactions.
          </Typography>
          <Typography variant="body2">
            Before starting your business planning, make sure your{" "}
            <strong>Brokerage Anniversary</strong> blocks in{" "}
            <strong>Financial Settings</strong> are up to date. If you recently
            added new transactions, click "<strong>Recalculate</strong>" in
            Financial Settings to ensure accurate projections.
          </Typography>
        </Alert>

        {/* Input Form Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={3}>
            {/* Target Annual Net Commission Income */}
            <Grid item xs={12} md={6}>
              <NumericFormat
                fullWidth
                valueIsNumericString
                customInput={TextField}
                thousandSeparator
                onValueChange={(values) => setTargetNetIncome(values.value)}
                allowLeadingZeros={false}
                decimalScale={0}
                value={targetNetIncome}
                label="Target Annual Net Commission Income"
                allowNegative={false}
                placeholder="Enter your target income"
                helperText="Your desired take-home commission income for the year"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
                inputProps={{
                  maxLength: 15,
                }}
              />
            </Grid>

            {/* Evaluate Based On (Year Selection) */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel shrink>Evaluate Based On</InputLabel>
                <Select
                  multiple
                  value={selectedYearIds}
                  displayEmpty
                  notched
                  onChange={(e) =>
                    setSelectedYearIds(e.target.value as number[])
                  }
                  label="Evaluate Based On"
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return <em>Select years to evaluate</em>;
                    }
                    return selected
                      .map((id) => years.find((y) => y.id === id)?.year)
                      .join(", ");
                  }}
                  MenuProps={{
                    PaperProps: { style: { maxHeight: 300 } },
                  }}
                >
                  {sortedYears.map((year) => (
                    <MenuItem key={year.id} value={year.id}>
                      <Checkbox
                        checked={
                          selectedYearIds.indexOf(year.id as number) > -1
                        }
                      />
                      <ListItemText primary={year.year} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Select historical years to base your projections on
              </Typography>
            </Grid>

            {/* Forecast For */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Forecast For</InputLabel>
                <Select
                  value={forecastYear}
                  onChange={(e) =>
                    setForecastYear(e.target.value as "thisYear" | "nextYear")
                  }
                  label="Forecast For"
                >
                  <MenuItem value="thisYear">
                    Current Year - Projected and Actual (
                    {new Date().getFullYear()})
                  </MenuItem>
                  <MenuItem value="nextYear">
                    Next Year - Projected ({new Date().getFullYear() + 1})
                  </MenuItem>
                </Select>
              </FormControl>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {forecastYear === "thisYear"
                  ? "Shows 'Transactions Completed'"
                  : "Full year projection without YTD adjustments"}
              </Typography>
            </Grid>

            {/* Page-level Co-Agent Filter (hidden when global filter is active or coAgent category is hidden) */}
            {!isGlobalFilterActive &&
              !hiddenFields?.some((field) => field.name === "coAgent") && (
                <Grid item xs={12} md={6}>
                  <CoAgentFilterSelect
                    coAgents={coAgents}
                    selectedCoAgentIds={effectiveCoAgentIds}
                    onChange={setPageCoAgentIds}
                    label="Filter by Team Members"
                    size="medium"
                  />
                </Grid>
              )}

            {/* Set Goals by Category */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel shrink>Set Goals by Category</InputLabel>
                <Select
                  multiple
                  value={selectedCategories}
                  displayEmpty
                  notched
                  onChange={handleCategoryChange}
                  label="Set Goals by Category"
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((cat) => (
                        <Chip
                          key={cat}
                          label={categoryLabels[cat]}
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: { style: { maxHeight: 300 } },
                  }}
                >
                  {(
                    Object.keys(
                      categoryLabels,
                    ) as BusinessPlanningCategoryType[]
                  ).map((cat) => (
                    <MenuItem
                      key={cat}
                      value={cat}
                      disabled={
                        selectedCategories.length >= 3 &&
                        !selectedCategories.includes(cat)
                      }
                    >
                      <Checkbox checked={selectedCategories.includes(cat)} />
                      <ListItemText
                        primary={categoryLabels[cat]}
                        secondary={categoryDescriptions[cat]}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Select up to 3 categories to break down your goals
              </Typography>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box
                sx={{ display: "flex", gap: 2, justifyContent: "flex-start" }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<PlanNowIcon />}
                  onClick={handlePlanNow}
                  disabled={!isFormValid || businessPlanning?.loading}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                  }}
                >
                  PLAN NOW!
                </Button>
                {hasCalculated && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Error Display */}
        {businessPlanning?.error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {businessPlanning.error}
          </Alert>
        )}

        {/* Results Section */}
        {projections && hasCalculated && (
          <>
            {/* Warning when no brokerage split is configured */}
            {!projections.brokerageAnniversary && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>No brokerage settings found.</strong> Your Projected
                  GCI equals your Target Net Income because no brokerage split
                  is configured. To get accurate projections, please configure
                  your brokerage anniversary settings in{" "}
                  <strong>Settings → Financial Settings</strong>.
                </Typography>
              </Alert>
            )}

            {/* Warning when brokerage split is 0 or not set */}
            {projections.brokerageAnniversary &&
              !projections.brokerageAnniversary.brokerageSplit && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Brokerage split is set to 0%.</strong> Your
                    Projected GCI equals your Target Net Income. If your
                    brokerage takes a percentage of your commission, please
                    update your brokerage split in{" "}
                    <strong>Settings → Financial Settings</strong>.
                  </Typography>
                </Alert>
              )}

            {/* Save as PDF Button - outside resultsRef so it won't appear in PDF */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<PdfIcon />}
                onClick={handleExportPdf}
                disabled={isExportingPdf}
              >
                Save as PDF
              </Button>
            </Box>

            <Box ref={resultsRef}>
              {/* Projected GCI Summary */}
              <Paper
                elevation={2}
                sx={{ p: 3, mb: 4, backgroundColor: "#f5f9ff" }}
              >
                <Box>
                  <Typography variant="h6" color="primary" gutterBottom>
                    <TrendingUpIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                    Projected GCI
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color="primary">
                    {formatCurrency(projections.projectedGci)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    To achieve{" "}
                    <strong>
                      {formatCurrency(projections.targetNetIncome)}
                    </strong>{" "}
                    net commission income
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Based on{" "}
                    {projections.brokerageAnniversary?.brokerageSplit
                      ? `${
                          projections.brokerageAnniversary.brokerageSplit
                        }% brokerage split${
                          projections.brokerageAnniversary.splitCap != null
                            ? ` (capped at ${formatCurrency(
                                projections.brokerageAnniversary.splitCap,
                              )})`
                            : ""
                        }`
                      : "no brokerage split"}
                    {projections.brokerageAnniversary
                      ?.additionalChargePercentage
                      ? `, additional ${
                          projections.brokerageAnniversary
                            .additionalChargePercentage
                        }% brokerage charge${
                          projections.brokerageAnniversary.additionalChargeCap
                            ? ` (capped at ${formatCurrency(
                                projections.brokerageAnniversary
                                  .additionalChargeCap,
                              )})`
                            : ""
                        }`
                      : ""}
                    , and referral fees, if any.
                  </Typography>
                </Box>
              </Paper>

              {/* Notes */}
              <Alert severity="info" sx={{ mb: 4 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> Categories contributing less than 5% to
                  your GCI are grouped into "Other" for clarity. Average prices
                  and commission percentages are based on your selected
                  evaluation years ({selectedYearText}).
                </Typography>
              </Alert>

              {/* Category Projections */}
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                Category Breakdown
              </Typography>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {projections.categoryProjections.map((catProj) => (
                  <Grid
                    item
                    xs={12}
                    // Two per row on mid-size screens, where three cards are too narrow for all sub-columns
                    md={projections.categoryProjections.length > 1 ? 6 : 12}
                    lg={Math.max(
                      4,
                      Math.floor(12 / projections.categoryProjections.length),
                    )}
                    key={catProj.category}
                  >
                    <CategoryProjectionCard
                      categoryProjection={catProj}
                      projectedGci={projections.projectedGci}
                      showCompleted={forecastYear === "thisYear"}
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Trend Chart */}
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                  Projected GCI Trend
                </Typography>
                <BusinessPlanningTrendChart
                  projectedTrend={projections.projectedTrend}
                  currentYearTrend={
                    forecastYear === "thisYear"
                      ? projections.currentYearTrend
                      : undefined
                  }
                  underContractGci={
                    forecastYear === "thisYear"
                      ? businessPlanning?.data?.underContractGci
                      : undefined
                  }
                  underContractLastMonth={
                    forecastYear === "thisYear"
                      ? businessPlanning?.data?.underContractLastMonth
                      : undefined
                  }
                  currentGciWithUnderContract={
                    forecastYear === "thisYear"
                      ? businessPlanning?.data?.currentGciWithUnderContract
                      : undefined
                  }
                  pipelineGci={
                    forecastYear === "thisYear"
                      ? businessPlanning?.data?.pipelineGci
                      : undefined
                  }
                  pipelineLastMonth={
                    forecastYear === "thisYear"
                      ? businessPlanning?.data?.pipelineLastMonth
                      : undefined
                  }
                  currentGciWithUnderContractAndPipeline={
                    forecastYear === "thisYear"
                      ? businessPlanning?.data
                          ?.currentGciWithUnderContractAndPipeline
                      : undefined
                  }
                  forecastYear={
                    forecastYear === "thisYear"
                      ? new Date().getFullYear()
                      : new Date().getFullYear() + 1
                  }
                />
              </Paper>

              {/* Additional Notes */}
              {/* <Alert severity="warning" sx={{ mt: 4 }}>
              <Typography variant="body2">
                <strong>Important:</strong> These projections are estimates
                based on your historical data. Actual results may vary based on
                market conditions, commission rates, and deal flow. Only
                Buyer/Seller transactions are included in these calculations.
              </Typography>
            </Alert> */}
            </Box>

            {/* Save as PDF Button - bottom */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<PdfIcon />}
                onClick={handleExportPdf}
                disabled={isExportingPdf}
              >
                Save as PDF
              </Button>
            </Box>
          </>
        )}
      </Box>
    </PageLayout>
  );
};

export default BusinessPlanning;
